// Type definitions matching backend models from startupfuel/models

export type ModelDate = string;

// ============================================
// Affiliation Model
// ============================================
export type AffiliationType =
  | "manager"
  | "member"
  | "company manager"
  | "judge";

export type AffiliationStatus = "pending" | "approved" | "declined";

export interface IAffiliation {
  _id: string;
  user: string;
  company?: string;
  opportunity?: string;
  type?: AffiliationType;
  status?: AffiliationStatus;
  role?: string;
  created_on: ModelDate;
}

// ============================================
// User Model
// ============================================
export type UserType =
  | "General User"
  | "Referral Partner"
  | "Community Manager"
  | "Admin";

export type UserStatus = "Active" | "Inactive" | "Suspended" | "Deleted";

export interface IUser {
  _id: string;
  user_type: UserType;
  first_name?: string;
  last_name?: string;
  short_bio?: string;
  phone_number?: string;
  email: string;
  referred_by?: string;
  referrer_source?: string;
  password?: string;
  street_number?: number;
  street_name?: string;
  street_type?: string;
  city?: string;
  province?: string;
  country: string;
  sub_vertical?: string;
  user_company_role?: string;
  isVerified: boolean;
  onboarding_status: {
    deals_page: boolean;
    deals_popup: boolean;
  };
  emailVerificationToken?: string;
  emailVerificationExpires?: ModelDate;
  created_a_company_once: boolean;
  receive_follow_up_registration_email: boolean;
  status: UserStatus;
  created_on: ModelDate;
  resetPasswordToken?: string;
  resetPasswordExpires?: ModelDate;
}

// ============================================
// Company Model
// ============================================
export type CompanyStatus = "Active" | "Inactive" | "Suspended" | "Deleted";

export interface ICompany {
  _id: string;
  company_name?: string;
  ratings?: string;
  company_file_ids?: Array<{ fileType: string; fileId: string }>;
  subscription_deal_limit: number;
  content: {
    extracted: Map<string, string>;
    anonymized: Map<string, string>;
  };
  company_type?: string[];
  street_number?: number;
  short_bio?: string;
  meta_short_bio?: string;
  detailed_description?: string;
  company_website?: string;
  street_name?: string;
  street_type?: string;
  prospective_aff_id?: string;
  prospective_aff_email?: string;
  prospective_aff_code?: string;
  distinction?: string[];
  industry?: string[];
  additional_Info?: any;
  business_overview?: string;
  business_CEO?: string;
  business_Product?: string;
  business_Finance?: string;
  business_Marketing?: string;
  business_Sales?: string;
  Captable_overview?: any;
  discount?: string;
  stage?: number;
  post_Cap?: string;
  Convertible?: string;
  Ticket_Size?: string;
  hard_Committed?: string;
  soft_Committed?: string;
  soft_Committed_founder?: string;
  business_model?: string;
  city?: string;
  province?: string;
  country: string;
  most_active_index: number;
  allow_investor?: boolean;
  stealth?: boolean;
  allow_social_announcements?: boolean;
  manual_team_list?: object[];
  lat?: number[];
  long?: number[];
  formatted_address?: string;
  address?: string;
  facebook?: string;
  twitter?: string;
  linkedIn?: string;
  instagram?: string;
  iOS_app_url?: string;
  android_app_url?: string;
  pitch_video_link?: string;
  pitch_comp_video_link?: string;
  background_video_link?: string;
  looking_for?: string[];
  objective?: string;
  problem?: string;
  target_market?: string;
  market_size?: string;
  solution?: string;
  competitors?: string;
  advantage?: string;
  differentiation?: string;
  revenue_model?: string;
  revenue_since_inception?: string;
  growth?: string;
  traction?: string;
  marketing_strategy?: string;
  team_description?: string;
  attempting_to_raise?: number;
  currency?: string;
  last_raise?: string;
  evaluation?: number;
  raising_funds?: string;
  date_of_inception?: ModelDate;
  month_of_inception?: string;
  year_of_inception?: string;
  csrf_token?: string;
  stripe_id?: string;
  customer_stripe_id?: string;
  settings?: object;
  referrer_source?: string;
  phone_number?: string;
  contact_phone?: string;
  contact_email?: string;
  corporate_structure?: string;
  other_industry?: string;
  other_stage?: string;
  other_company_type?: string;
  problem_or_opportunity?: string;
  value_proposition?: string;
  go_to_market?: string;
  risk?: string;
  user_acquisition_cost?: string;
  customer_lifetime_value?: string;
  market_opportunity?: string;
  moat?: string;
  why_now?: string;
  milestones?: string;
  business_plan?: string;
  use_of_funds?: string;
  committed?: string;
  traction_customers?: string;
  traction_revenue?: string;
  traction_partners?: string;
  integration?: string;
  customer_email?: string;
  product?: string;
  round?: string;
  raised?: string;
  raising?: string;
  minimum?: string;
  lead?: string;
  terms?: string;
  team?: string;
  advisors?: string;
  board?: string;
  fundraising_history?: string;
  status: CompanyStatus;
  created_on: ModelDate;
}

// ============================================
// Workspace Model (maps to Opportunity in backend)
// ============================================
export interface IStatus {
  colour: string;
  title: string;
  description: string;
  order: number;
}

export interface IFormField {
  title: string;
  helper_text: string;
  display: boolean;
  required: boolean;
}

export interface IOpportunityForm {
  [key: string]: IFormField;
}

export interface IPortcoForm {
  [key: string]: IFormField;
}

export interface IOpportunityFormReal {
  short_bio: IFormField;
  objective: IFormField;
  problem: IFormField;
  solution: IFormField;
  target_market: IFormField;
  market_size: IFormField;
  revenue_model: IFormField;
  competitors: IFormField;
  advantage: IFormField;
  team_description: IFormField;
  pitch_video_link: IFormField;
}

export interface IPortcoFormReal {
  team_size: IFormField;
  revenue: IFormField;
  funding_stage: IFormField;
  total_funding: IFormField;
  growth_metrics: IFormField;
  milestones: IFormField;
  challenges: IFormField;
  next_steps: IFormField;
}

export type TaskName =
  | "add_deal"
  | "add_document"
  | "analyze_website"
  | "deal_info"
  | "tearsheet"
  | "add_team_member"
  | "add_thesis"
  | "startup_score";

export interface ITask {
  name?: TaskName;
  completed: boolean;
  credits?: number;
  completed_on?: ModelDate;
}

export interface IWorkspace {
  _id: string;
  from?: string;
  opportunity_type: string;
  opportunity_name?: string;
  background_cover?: string;
  affiliated_company?: {
    _id: string;
    company_name: string;
    files: IFileUpload[];
  };
  target_industries?: string[];
  target_gics_industries?: string[];
  target_gics_sub_industries?: string[];
  target_stage_companies?: string[];
  target_model_companies?: string[];
  target_revenue_models?: string[];
  target_distinctions?: string[];
  opportunity_description?: string;
  website_url?: string;
  start_intake_date?: ModelDate;
  end_intake_date?: ModelDate;
  min_cheque_size: number;
  max_cheque_size: number;
  currency?: string;
  geographical_location?: string[];
  /** The thesis as the investor wrote it — the input Analyze Thesis derives from. */
  investment_thesis?: string;
  /** The AI's prose summary of the thesis, user-editable. */
  additional_info?: string;
  /** Criteria the structured target_* fields cannot express. */
  thesis_inclusions?: string[];
  thesis_exclusions?: string[];
  /** Depth new deals are analysed at. Defaults to "full" server-side. */
  default_diligence_level?: "light" | "full";
  prize?: string;
  statuses: IStatus[];
  tasks?: ITask[];
  amount?: number;
  has_applicant_room: boolean;
  judges_get_email: boolean;
  show_judge_on_opportunity: boolean;
  opportunity_private: boolean;
  opportunity_form_modify: boolean;
  opportunity_form: IOpportunityForm;
  portco_form: IPortcoForm;
  is_paying: boolean;
  questionnaire?: string[];
  team_members?: Array<{
    first_name: string;
    last_name: string;
    role: string;
  }>;
  portal_slug?: string;
  raise_board_enabled?: boolean;
  lookbooks_enabled?: boolean;
  inbound_email_enabled?: boolean;
  inbound_email_slug?: string;
  inbound_email_auto_create_deals?: boolean;
  created_on: ModelDate;
}

// ============================================
// Deal Model (maps to OpportunityApplication in backend)
// ============================================
export type DealActionType = "pending" | "approved" | "declined" | "reviewed";
export type DealFieldState = "approved" | "declined" | "pending";
export type ReportStatusType =
  | "pending"
  | "completed"
  | "in_progress"
  | "failed"
  | "generating_main_webpage_info"
  | "generating_secondary_webpage_info"
  | `generating_${string}`;

export interface IDealInfo {
  field_name: string;
  field_value: string;
  field_state: DealFieldState;
}

export interface IReportStatus {
  website_summary_v2?: ReportStatusType;
  website_summary_v2_ingest?: ReportStatusType;
  migration?: ReportStatusType;
  deal_info?: ReportStatusType;
  deal_info_ingest?: ReportStatusType;
  datasufficiencyscore_v2?: ReportStatusType;
  tearsheet?: ReportStatusType;
  // Legacy fields for backward compatibility
  summary?: ReportStatusType;
  datasufficiencyscore?: ReportStatusType;
  startupscore?: ReportStatusType;
  website_summary?: ReportStatusType;
  metrics?: ReportStatusType;
  website_summary_ingest?: ReportStatusType;
  gap_analysis?: ReportStatusType;
  deal_fit?: ReportStatusType;
}

export interface IFundraising {
  total_amount?: string;
  committed_amount?: string;
  lead_investor?: string;
}

export interface IDealNote {
  _id: string;
  note?: string;
  tags?: string[];
  created_on: ModelDate;
  created_by_user_id?: string;
  created_by_user?: string;
}

export interface IQuestionnaire {
  question?: string;
  answer?: string;
}

export interface IDeal {
  _id: string;
  applicant_company?: string;
  pertains_to_opportunity?: string;
  changed_by?: string;
  custom_text_a1?: string;
  custom_text_a2?: string;
  application_status?: string;
  action_type?: DealActionType;
  created_on: ModelDate;
  deal_info?: IDealInfo[];
  fundraising?: IFundraising;

  // Business and Team Information
  business_CEO?: string;
  business_Product?: string;
  business_Finance?: string;
  business_Marketing?: string;
  business_Sales?: string;
  business_overview?: string;
  business_model?: string;

  // Market and Strategy Information
  objective?: string;
  problem?: string;
  target_market?: string;
  market_size?: string;
  solution?: string;
  competitors?: string;
  differentiation?: string;
  revenue_model?: string;
  revenue_since_inception?: string;
  growth?: string;
  traction?: string;
  marketing_strategy?: string;
  team_description?: string;

  // Financial and Investment Information
  attempting_to_raise?: string;
  currency?: string;
  last_raise?: string;
  evaluation?: string;
  discount?: string;
  post_Cap?: string;
  Convertible?: string;
  Ticket_Size?: string;
  hard_Committed?: string;
  soft_Committed?: string;
  soft_Committed_founder?: string;
  Captable_overview?: string;
  raising_funds?: string;

  // Pitchbox-specific fields
  pitch_comp_video_link?: string;
  ratings?: string;
  finance_mtl_q1?: string;
  finance_mtl_q2?: string;
  finance_mtl_q3?: string;
  finance_mtl_q4?: string;
  additional_Info?: string;
  incubator_accelerator_history?: string;

  // Company Details
  company_website?: string;
  short_bio?: string;
  industry?: string[];
  sub_vertical?: string;
  stage?: number;
  stealth?: boolean;
  street_number?: number;
  street_name?: string;
  city?: string;
  province?: string;
  country?: string;
  address?: string;
  lat?: number[];
  long?: number[];
  formatted_address?: string;
  contact_email?: string;
  contact_phone?: string;
  facebook?: string;
  twitter?: string;
  linkedIn?: string;
  instagram?: string;
  date_of_inception?: ModelDate;
  month_of_inception?: string;
  year_of_inception?: string;
  prospective_aff_id?: string;
  prospective_aff_email?: string;
  prospective_aff_code?: string;
  referrer_source?: string;

  website_summary?: {
    website_summary?: string;
    created_on?: ModelDate;
  };
  deal_notes?: IDealNote[];
  questionnaire?: IQuestionnaire[];
  source?: string;
  report_status?: IReportStatus;
  ai_tracked?: boolean;
}

// ============================================
// InboundEmail Model
// ============================================
export type InboundEmailStatus = "needs_review" | "linked" | "ignored";

/**
 * What the inbox can be narrowed to.
 *
 * `needs_info` is not a status — an email the automatic run could not read is
 * still in the review pile, and moving it out would hide it from anyone not
 * looking for it. It is a view over that pile.
 */
export const NEEDS_INFO_FILTER = "needs_info";
export type InboxFilter = InboundEmailStatus | typeof NEEDS_INFO_FILTER;

export interface IInboundEmailAttachment {
  file_name: string;
  file_url: string;
  file_size?: number;
  content_type?: string;
  file_upload_id?: string;
}

export interface IInboundEmailExtractedInfo {
  company_name?: string;
  founder_names?: string;
  founder_emails?: string;
  stage?: string;
  description?: string;
  funding_ask?: string;
  traction?: string;
  website?: string;
  file_url?: string;
  notes?: string;
  sub_industries?: number[];
  low_confidence?: boolean;
  processed_at?: string;
  status?: "pending" | "processing" | "completed" | "failed" | "low_confidence";
}

export interface IInboundEmail {
  _id: string;
  workspace: string;
  from_email: string;
  from_name?: string;
  to: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  text?: string;
  html?: string;
  attachments: IInboundEmailAttachment[];
  status: InboundEmailStatus;
  linked_deal?: {
    _id: string;
    applicant_company?: string;
    application_status?: string;
  };
  linked_company?: {
    _id: string;
    company_name?: string;
  };
  thread_id?: string;
  message_id?: string;
  in_reply_to?: string;
  received_at: ModelDate;
  createdAt: ModelDate;
  updatedAt: ModelDate;
  extracted_info?: IInboundEmailExtractedInfo;
  /** Present once the message has been edited; the as-received original. */
  original_subject?: string;
  original_text?: string;
  original_html?: string;
  content_edited_at?: string;
  /**
   * Whether to offer creating a deal from this email. Derived per request by
   * the server on both the list and detail reads, never stored — see
   * `isAutoCreateSuggested`.
   */
  auto_create_suggested?: boolean;
  /**
   * What the automatic run did once the extraction finished. Unlike
   * `auto_create_suggested` this *is* stored — it records an action taken, not
   * a judgement that can be re-derived. Absent means the run never reached this
   * email: the setting was off, or it arrived before the feature existed.
   */
  auto_create_outcome?: "created" | "insufficient_info" | "failed";
  /** Why, in a sentence. Shown on the badge. */
  auto_create_reason?: string;
  auto_create_at?: string;
}

// ============================================
// AiGeneration Model
// ============================================
export interface IScoreItem {
  Score?: number;
  Reason?: string;
}

export interface IStartupScore {
  Idea?: IScoreItem;
  Tech?: IScoreItem;
  Team?: IScoreItem;
  Marketing?: IScoreItem;
  Traction?: IScoreItem;
  Overall?: IScoreItem;
}

export interface IDataSufficiencyScore {
  Idea?: IScoreItem;
  Tech?: IScoreItem;
  Team?: IScoreItem;
  Marketing?: IScoreItem;
  Traction?: IScoreItem;
  Overall?: IScoreItem;
}

export interface ISummary {
  Idea?: string;
  Tech?: string;
  Marketing?: string;
  Team?: string;
  BusinessModel?: string;
  Traction?: string;
}

export interface ITearSheet {
  Overview?: string;
  Problem?: string;
  Solution?: string;
  Market?: string;
  Traction?: string;
  Team?: string;
  Advantage?: string;
  KeyHighlights?: string[];
  ModelDateCreated?: ModelDate;
}

export interface IChat {
  _id: string;
  userId?: string;
  userQuestion?: string;
  aiResponse?: string;
  timestamp: ModelDate;
}

// New chat thread message types
export interface IChatMessageData {
  content?: string;
  additional_kwargs?: Record<string, any>;
  response_metadata?: {
    finish_reason?: string;
    model_name?: string;
    system_fingerprint?: string;
    service_tier?: string;
  };
  type?: "human" | "ai";
  name?: string | null;
  id?: string;
  example?: boolean;
  tool_calls?: any[];
  invalid_tool_calls?: any[];
  usage_metadata?: any;
}

export interface IChatMessage {
  type: "human" | "ai";
  data: IChatMessageData;
}

export interface IChatThread {
  _id: string;
  messages: IChatMessage[];
  opportunity_application_id: string;
  thread_name?: string;
  messages_history?: Array<{
    node: "human" | "ai" | "tool";
    content: string;
    name?: string | null;
    id?: string | null;
  }>;
  summary?: string;
  updated_on: string | Date;
}

// Tool call data with reasoning fields and status
export interface IToolCallData {
  name: string;
  node?: string;
  status?: "invoking" | "completed";
  result?: string;
  // Dynamic key-value pairs for reasoning/text fields
  [key: string]: string | undefined;
}

export interface IDealMemoCategory {
  name: string;
  score: number;
  description: string;
  evaluation: string;
}

export type DealMemoStatus = "pending" | "completed";

export interface IDealMemo {
  overallScore: number;
  summary: string;
  overallEvaluation: string;
  categories?: IDealMemoCategory[];
  status?: DealMemoStatus;
}

export interface IMarketSummary {
  Key_Market_Drivers: string[];
  Competitive_Landscape: string;
  Growth_Opportunities: string[];
}

export interface IMarketSize {
  TAM_size: number;
  TAM_potential: string[];
  TAM_scope: string[];
  TAM_context: string;
  SAM_size: number;
  SAM_potential: string[];
  SAM_scope: string[];
  SAM_context: string;
  SOM_size: number;
  SOM_potential: string[];
  SOM_scope: string[];
  SOM_context: string;
}

export interface IMarketIntelligence {
  Market_Summary: IMarketSummary;
  Market_Size: IMarketSize;
}

export interface IMetricDataPoint {
  title: string;
  value: string | number;
}

export interface IMetricSource {
  file_url: string;
  file_type: string;
  source_type: string;
  source_description: string | number;
}

export interface IMetricItem {
  title: string;
  sub_category: string;
  category: string;
  sources: IMetricSource[];
  data: IMetricDataPoint[];
}

export interface IGapAnalysis {
  _id: string;
  gap_analysis: string | Record<string, unknown> | unknown[];
  created_on: string;
  credits_used?: number;
}

export interface IDealFitScoreBreakdown {
  score: number;
  matches: string[];
  target?: string[];
}

export interface IDealFit {
  _id: string;
  deal_fit: {
    score: number;
    breakdown: {
      businessModel: IDealFitScoreBreakdown;
      stage: IDealFitScoreBreakdown;
      country: IDealFitScoreBreakdown;
      industry: IDealFitScoreBreakdown;
      distinction: IDealFitScoreBreakdown;
      fundraising: IDealFitScoreBreakdown;
    };
  };
  created_on: string;
  credits_used?: number;
}

export interface IMetricsReport {
  _id: string;
  company_id: string;
  pertains_to_opportunity: string;
  opportunity_application_id: string;
  metrics: IMetricItem[];
  created_on: string;
}

export interface IAiGeneration {
  _id: string;
  company_id?: string;
  pertains_to_opportunity?: string;
  startupscore?: IStartupScore;
  datasufficiencyscore?: IDataSufficiencyScore;
  datasufficiencyscore_v2?: IDataSufficiencyScoreV2;
  summary?: ISummary;
  tearsheet?: ITearSheet;
  chat?: IChat[];
  metrics?: IMetricItem[];
  market_intel?: IMarketIntelligence;
  dealMemo?: IDealMemo;
  valuations?: IValuation[];
  gap_analysis?: IGapAnalysis[];
  deal_fit?: IDealFit[];
  created_on: ModelDate;
}

// ============================================
// FileUpload Model
// ============================================
export const VALID_FILE_TYPES = [
  "company pitchdeck",
  "website_content",
  "company avatar",
  "company background",
  "company financial report",
  "market problem",
  "product service deck",
  "competitive landscape",
  "capitalization table",
  "financial details",
  "round details",
  "articles of incorporation",
  "legal counsel contact info",
  "intellectual property",
  "domain names",
  "founder bios",
  "founder employment agreements",
  "key personnel bios",
  "key personnel employment agreements",
  "organization chart",
  "business advisory board",
  "advisor compensation",
  "market overview",
  "market segmentation",
  "product market fit",
  "research and development",
  "products and pipeline",
  "business model",
  "customer acquisition",
  "major customers and partners",
  "customer contracts service agreements",
  "key competitors and competitive advantage",
  "funding history",
  "non-dilutive funding",
  "business plan",
] as const;

export type ValidFileType = (typeof VALID_FILE_TYPES)[number];

export interface IFileUpload {
  _id: string;
  file_name: string;
  file_size?: string;
  user?: string;
  company?: string;
  product?: string;
  service?: string;
  file_description?: string;
  file_type: ValidFileType;
  file_url: string;
  time_stamp: ModelDate;
  // Virtual fields
  file_name_ext?: string;
  file_url_optimized_500_300?: string;
}

// ============================================
// Valuation Score Model
// ============================================
export interface IValuationScore {
  _id: string;
  Stage: number;
  Score: number;
  Post_Money_Valuation: string;
  Cash_Raised: string;
  Dilution: string;
  created_on: ModelDate;
  updated_on: ModelDate;
}

// ============================================
// Valuation Model (Updated)
// ============================================
export interface IValuation {
  _id?: string;
  valuationLow: number;
  valuationMid: number;
  valuationHigh: number;
  cashRaised: number;
  dilution: number;
  valuationMethod: string;
  startupScore?: IStartupScore;
  dataSufficiencyScore?: IDataSufficiencyScore;
  gapAnalysisId?: string;
  gapAnalysisScore?: number;
  categoryScores?: Record<string, any>;
  stage: string;
  created_on: ModelDate;
}

// ============================================
// Valuation Calculation Details
// ============================================
export interface IBenchmarkDataPoint {
  decile: number;
  value: number;
  isCurrentStartup: boolean;
}

export interface IBenchmarkData {
  valuation: IBenchmarkDataPoint[];
  cashRaised: IBenchmarkDataPoint[];
  dilution: IBenchmarkDataPoint[];
}

export interface IValuationCalculationDetails {
  finalScore: number;
  stage: number;
  categoryScores: Record<
    string,
    {
      startupScore: number;
      dataSufficiencyScore: number;
      weight: number;
      weightedScore: number;
    }
  >;
  totalDataSufficiency: number;
  dataSufficiencySource?: "gap_analysis" | "data_sufficiency_v1" | "data_sufficiency_v2";
  gapAnalysisId?: string;
  gapAnalysisScore?: number;
  decile: number;
  benchmarkData: IBenchmarkData;
  valuationScoreUsed: {
    score: number;
    postMoneyValuation: string;
    cashRaised: string;
    dilution: string;
  };
}

// ============================================
// Startup Score Model
// ============================================
export interface IStartupScore {
  Idea?: {
    Score?: number;
    Reason?: string;
  };
  Tech?: {
    Score?: number;
    Reason?: string;
  };
  Team?: {
    Score?: number;
    Reason?: string;
  };
  Marketing?: {
    Score?: number;
    Reason?: string;
  };
  Traction?: {
    Score?: number;
    Reason?: string;
  };
  Overall?: {
    Score?: number;
    Reason?: string;
  };
}

// ============================================
// Data Sufficiency Score Model
// ============================================
export interface IDataSufficiencyScore {
  Idea?: {
    Score?: number;
    Reason?: string;
  };
  Tech?: {
    Score?: number;
    Reason?: string;
  };
  Team?: {
    Score?: number;
    Reason?: string;
  };
  Marketing?: {
    Score?: number;
    Reason?: string;
  };
  Traction?: {
    Score?: number;
    Reason?: string;
  };
  Overall?: {
    Score?: number;
    Reason?: string;
  };
}

// ============================================
// Data Sufficiency Score V2 Model
// ============================================
export interface IDataSufficiencyScoreV2Source {
  file_url: string;
  file_type: string;
  source_type: string;
  source_description: string | number;
}

export interface IDataSufficiencyScoreV2 {
  // 1) Problem / solution
  "problem/solution"?: {
    score_report: Array<{
      question: string;
      score: number;
      reasoning: string;
      sources?: IDataSufficiencyScoreV2Source[];
    }>;
    final_score: number;
    missing_report: Array<{
      question: string;
      score: number;
      reasoning: string;
      sources?: IDataSufficiencyScoreV2Source[];
    }>;
  };
  // 2) Product /service
  "product/service"?: {
    score_report: Array<{
      question: string;
      score: number;
      reasoning: string;
      sources?: IDataSufficiencyScoreV2Source[];
    }>;
    final_score: number;
    missing_report: Array<{
      question: string;
      score: number;
      reasoning: string;
      sources?: IDataSufficiencyScoreV2Source[];
    }>;
  };
  // 3) Team
  team?: {
    score_report: Array<{
      question: string;
      score: number;
      reasoning: string;
      sources?: IDataSufficiencyScoreV2Source[];
    }>;
    final_score: number;
    missing_report: Array<{
      question: string;
      score: number;
      reasoning: string;
      sources?: IDataSufficiencyScoreV2Source[];
    }>;
  };
  // 4) Marketing
  marketing?: {
    score_report: Array<{
      question: string;
      score: number;
      reasoning: string;
      sources?: IDataSufficiencyScoreV2Source[];
    }>;
    final_score: number;
    missing_report: Array<{
      question: string;
      score: number;
      reasoning: string;
      sources?: IDataSufficiencyScoreV2Source[];
    }>;
  };
  // 5) Traction
  traction?: {
    score_report: Array<{
      question: string;
      score: number;
      reasoning: string;
      sources?: IDataSufficiencyScoreV2Source[];
    }>;
    final_score: number;
    missing_report: Array<{
      question: string;
      score: number;
      reasoning: string;
      sources?: IDataSufficiencyScoreV2Source[];
    }>;
  };
  // 6) Finance/ round
  "finance/round"?: {
    score_report: Array<{
      question: string;
      score: number;
      reasoning: string;
      sources?: IDataSufficiencyScoreV2Source[];
    }>;
    final_score: number;
    missing_report: Array<{
      question: string;
      score: number;
      reasoning: string;
      sources?: IDataSufficiencyScoreV2Source[];
    }>;
  };
  // 7) Legal
  legal?: {
    score_report: Array<{
      question: string;
      score: number;
      reasoning: string;
      sources?: IDataSufficiencyScoreV2Source[];
    }>;
    final_score: number;
    missing_report: Array<{
      question: string;
      score: number;
      reasoning: string;
      sources?: IDataSufficiencyScoreV2Source[];
    }>;
  };
  overall_score?: number;
}

// ============================================
// Valuations API Response
// ============================================
export interface IValuationsResponse {
  status: string;
  data: {
    valuations?: Array<{
      _id: string;
      valuations: IValuation[];
      created_on: ModelDate;
    }>;
  };
}

// ============================================
// Generate Valuations Response
// ============================================
export interface IGenerateValuationsResponse {
  status: string;
  message: string;
  data: {
    valuation: IValuation;
    calculationDetails: IValuationCalculationDetails;
  };
}

// ============================================
// Decile Benchmarks Response
// ============================================
export interface IDecileBenchmarksResponse {
  status: string;
  message: string;
  data: {
    startupDecile: number;
    finalScore: number;
    benchmarkData: IBenchmarkData;
  };
}
